import { useState, useMemo, useEffect, useRef } from "react";
import { Routes, Route, Navigate, useLocation } from "react-router-dom";
import { jwtDecode } from "jwt-decode";

// @mui material components
import { ThemeProvider } from "@mui/material/styles";
import CssBaseline from "@mui/material/CssBaseline";
import { useNavigate } from "react-router-dom";

// Material Dashboard 2 React components
import Sidenav from "examples/Sidenav";
import BottomNav from "examples/BottomNav";
import Box from "@mui/material/Box";

// Material Dashboard 2 React themes
import theme from "assets/theme";
import themeRTL from "assets/theme/theme-rtl";
import themeDark from "assets/theme-dark";
import themeDarkRTL from "assets/theme-dark/theme-rtl";

// RTL plugins
import rtlPlugin from "stylis-plugin-rtl";
import { CacheProvider } from "@emotion/react";
import createCache from "@emotion/cache";

// Material Dashboard 2 React routes
import routes from "routes";
import { getAuthToken, logout } from "utils/authToken";
import apiClient from "utils/apiClient";

// Material Dashboard 2 React contexts
import { useMaterialUIController, setMiniSidenav } from "context";
import { UnitSystemProvider } from "context/UnitSystemContext";
import { ENDPOINTS } from "utils/endpoints";

// ProtectedRoute
import ProtectedRoute from "ProtectedRoute";

export default function App() {
  const [controller, dispatch] = useMaterialUIController();
  const { miniSidenav, direction, layout, sidenavColor, darkMode } = controller;

  const [onMouseEnter, setOnMouseEnter] = useState(false);
  const [rtlCache, setRtlCache] = useState(null);
  const [userRole, setUserRole] = useState(null);
  const [username, setUsername] = useState("");
  const { pathname } = useLocation();
  const navigate = useNavigate();
  const [distinction, setDistinction] = useState("");

  const handleLogout = () => {
    logout();
    navigate("/authentication/sign-in", { replace: true });
  };

  // -------------------
  // Decode token + check expiry on mount
  // -------------------
  useEffect(() => {
    const token = getAuthToken();

    if (token) {
      try {
        const decoded = jwtDecode(token);
        setUserRole(decoded.role);
        if (decoded.role === "admin" && !pathname.startsWith("/admin")) {
          navigate("/admin", { replace: true });
        }

        const now = Date.now() / 1000;
        if (decoded.exp && decoded.exp < now) {
          handleLogout();
        }
      } catch (error) {
        console.error("Failed to decode token:", error.message);
        handleLogout();
      }
    }
  }, []);

  // -------------------
  // Inactivity logout (30 min, resets on user activity)
  // -------------------
  useEffect(() => {
    if (!getAuthToken()) return;

    const TIMEOUT = 45 * 60 * 1000;
    let timer = setTimeout(handleLogout, TIMEOUT);

    const reset = () => {
      clearTimeout(timer);
      timer = setTimeout(handleLogout, TIMEOUT);
    };

    const events = ["click", "keydown", "mousemove", "touchstart", "scroll"];
    events.forEach((e) => window.addEventListener(e, reset, { passive: true }));

    return () => {
      clearTimeout(timer);
      events.forEach((e) => window.removeEventListener(e, reset));
    };
  }, []);

  // Which token the profile in state belongs to. App never unmounts, so a
  // sign-in or sign-up happens *under* an already-mounted App: this effect used
  // to run once with no token, return, and never run again, leaving the sidenav
  // reading "Welcome !" for the whole first session until something forced a
  // full page load. Re-checking on every navigation costs one comparison and
  // fires the fetch the moment a token appears.
  const profileTokenRef = useRef(null);

  useEffect(() => {
    const token = getAuthToken();
    if (!token) {
      profileTokenRef.current = null;
      setUsername("");
      setDistinction("");
      return;
    }
    if (profileTokenRef.current === token) return;
    profileTokenRef.current = token;

    const fetchUserData = async () => {
      try {
        const decoded = jwtDecode(token);
        if (decoded.role === "admin") return;

        const [userRes, statsRes] = await Promise.all([
          apiClient.get(ENDPOINTS.users.me),
          apiClient.get(ENDPOINTS.users.stats),
        ]);
        setUsername(userRes.data.username || "User");
        setDistinction(statsRes.data.distinction || "");
      } catch (err) {
        console.error("Failed to load user data:", err.message);
        setUsername("User");
        setDistinction("Novice");
      }
    };

    fetchUserData();
  }, [pathname]);

  // -----------------------------------------------------------

  useMemo(() => {
    const cacheRtl = createCache({
      key: "rtl",
      stylisPlugins: [rtlPlugin],
    });
    setRtlCache(cacheRtl);
  }, []);

  const handleOnMouseEnter = () => {
    if (miniSidenav && !onMouseEnter) {
      setMiniSidenav(dispatch, false);
      setOnMouseEnter(true);
    }
  };

  const handleOnMouseLeave = () => {
    if (onMouseEnter) {
      setMiniSidenav(dispatch, true);
      setOnMouseEnter(false);
    }
  };

  useMemo(() => {
    document.documentElement.scrollTop = 0;
    document.scrollingElement.scrollTop = 0;
  }, [pathname]);

  const getRoutes = (allRoutes) =>
    allRoutes.map((route) => {
      if (route.collapse) return getRoutes(route.collapse);
      if (route.route) {
        const element = route.protected ? (
          <ProtectedRoute roles={route.roles}>{route.component}</ProtectedRoute>
        ) : (
          route.component
        );
        return <Route exact path={route.route} element={element} key={route.key} />;
      }
      return null;
    });

  const hideSidenav =
    userRole === "admin" ||
    pathname.startsWith("/module") ||
    [
      "/authentication/sign-in",
      "/authentication/sign-up",
      "/terms",
      "/privacy",
      "/onboarding",
    ].includes(pathname);

  const filteredRoutes = useMemo(() => {
    return routes.filter((route) => {
      if (route.key === "Onboarding") return false;
      if (route.key === "admin") return userRole === "admin";
      return true;
    });
  }, [userRole]);

  // Until the profile lands there is no name to greet, and "Welcome !" reads as
  // a rendering bug rather than a loading state. The bare greeting is correct
  // at every moment and simply gains a name.
  const greeting = username ? `Welcome ${username}!` : "Welcome!";

  const ThemeComponent =
    direction === "rtl" ? (darkMode ? themeDarkRTL : themeRTL) : darkMode ? themeDark : theme;

  return direction === "rtl" ? (
    <CacheProvider value={rtlCache}>
      <ThemeProvider theme={ThemeComponent}>
        <UnitSystemProvider>
          <CssBaseline />
          {layout === "dashboard" && !hideSidenav && (
            <>
              <Box sx={{ display: { xs: "none", xl: "block" } }}>
                <Sidenav
                  color={sidenavColor}
                  username={greeting}
                  subtitle={distinction}
                  icon=""
                  routes={filteredRoutes}
                  onMouseEnter={handleOnMouseEnter}
                  onMouseLeave={handleOnMouseLeave}
                />
              </Box>
              <BottomNav />
            </>
          )}
          <Routes>
            {getRoutes(routes)}
            <Route
              path="*"
              element={<Navigate to={userRole === "admin" ? "/admin" : "/dashboard"} />}
            />
          </Routes>
        </UnitSystemProvider>
      </ThemeProvider>
    </CacheProvider>
  ) : (
    <UnitSystemProvider>
      <ThemeProvider theme={ThemeComponent}>
        <CssBaseline />
        {layout === "dashboard" && !hideSidenav && (
          <>
            <Box sx={{ display: { xs: "none", xl: "block" } }}>
              <Sidenav
                color={sidenavColor}
                username={greeting}
                subtitle={distinction}
                icon=""
                routes={filteredRoutes}
                onMouseEnter={handleOnMouseEnter}
                onMouseLeave={handleOnMouseLeave}
              />
            </Box>
            <BottomNav />
          </>
        )}
        <Routes>
          {getRoutes(routes)}
          <Route path="*" element={<Navigate to="/dashboard" />} />
        </Routes>
      </ThemeProvider>
    </UnitSystemProvider>
  );
}
